import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { concatMap, finalize, tap } from 'rxjs';
import { MdQueryApiService, mdQueryError } from './md-query-api.service';
import { CollectionRequest, IndexStats } from './md-query.models';
import { jkGlb } from 'jk-service';

@Component({
  selector: 'mdq-settings', standalone: true, imports: [DecimalPipe, FormsModule, RouterLink],
  templateUrl: './mdq-settings.component.html', styleUrl: './md-query.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MdqSettingsComponent {
  private readonly api = inject(MdQueryApiService);
  private readonly destroyRef = inject(DestroyRef);
  readonly apiBaseUrl = input<string>('');
  readonly apiToken = input('');
  readonly connection = computed(() => {
    const baseUrl = this.apiBaseUrl().trim();
    const token = this.apiToken().trim();
    return baseUrl && token ? { baseUrl, token } : undefined;
  });
  readonly healthResource = rxResource({ params: this.connection, stream: ({ params }) => this.api.getHealth(params) });
  readonly collectionsResource = rxResource({ params: this.connection, stream: ({ params }) => this.api.getCollections(params) });
  readonly health = computed(() => this.healthResource.hasValue() ? this.healthResource.value() : null);
  readonly collections = computed(() => this.collectionsResource.hasValue() ? this.collectionsResource.value().collections : []);
  readonly totalDocCount = computed(() => this.collections().reduce((sum, item) => sum + item.docCount, 0));
  readonly loadError = computed(() => {
    if (!this.connection()) return null;
    const error = this.healthResource.error() ?? this.collectionsResource.error();
    return error ? mdQueryError(error) : null;
  });
  readonly isIndexing = signal(false);
  readonly indexingStats = signal<IndexStats | null>(null);
  readonly errorMessage = signal<string | null>(null);
  readonly statusMessage = signal('');
  readonly newCollectionName = signal('');
  readonly newCollectionPath = signal('');
  readonly newCollectionPattern = signal('**/*.md');
  readonly isCreateModalOpen = signal(false);
  readonly createDialog = viewChild.required<ElementRef<HTMLDialogElement>>('createDialog');
  readonly collectionRegistered = output<string>();
  readonly collectionDeleted = output<string>();
  readonly indexCompleted = output<IndexStats>();
  readonly canCreate = computed(() => !!this.connection() && !!this.newCollectionName().trim() && !!this.newCollectionPath().trim() && !!this.newCollectionPattern().trim() && !this.isIndexing() && this.collectionsResource.hasValue() && !this.collectionsResource.isLoading());

  refresh(): void {
    if (!this.connection()) return;
    this.healthResource.reload();
    this.collectionsResource.reload();
  }
  openCreate(): void { this.isCreateModalOpen.set(true); this.createDialog().nativeElement.showModal(); }
  closeCreate(): void { this.createDialog().nativeElement.close(); this.isCreateModalOpen.set(false); }

  registerCollection(): void {
    const connection = this.connection();
    if (!connection || !this.canCreate()) return;
    const data: CollectionRequest = { name: this.newCollectionName().trim(), path: this.newCollectionPath().trim(), pattern: this.newCollectionPattern().trim() };
    if (this.collections().some(item => item.name === data.name)) {
      this.errorMessage.set('此集合名稱已存在，請使用其他名稱。');
      return;
    }
    this.beginOperation('正在註冊知識庫並建立首次索引…');
    this.api.addCollection(connection, data).pipe(
      tap(() => {
        this.collectionRegistered.emit(data.name);
        this.statusMessage.set('集合已註冊，正在建立首次索引…');
        this.closeCreate();
        this.newCollectionName.set(''); this.newCollectionPath.set(''); this.newCollectionPattern.set('**/*.md');
        this.refresh();
      }),
      concatMap(() => this.api.triggerReindex(connection, { collection: data.name, force: false })),
      takeUntilDestroyed(this.destroyRef), finalize(() => this.isIndexing.set(false)),
    ).subscribe({ next: response => this.finishIndex(response.stats), error: error => this.failOperation(error) });
  }
  reindex(collection?: string, force = false): void {
    const connection = this.connection();
    if (!connection || this.isIndexing()) return;
    this.beginOperation(`正在${force ? '重建' : '更新'}${collection || '全部集合'}索引…`);
    this.api.triggerReindex(connection, { ...(collection ? { collection } : {}), force }).pipe(
      takeUntilDestroyed(this.destroyRef), finalize(() => this.isIndexing.set(false)),
    ).subscribe({ next: response => this.finishIndex(response.stats), error: error => this.failOperation(error) });
  }
  deleteCollection(name: string): void {
    const connection = this.connection();
    if (!connection || this.isIndexing()) return;
    if (!window.confirm(`確定要刪除知識庫「${name}」嗎？此動作將清除該知識庫的所有索引資料。`)) {
      return;
    }
    this.beginOperation(`正在刪除知識庫「${name}」…`);
    this.api.deleteCollection(connection, name).pipe(
      takeUntilDestroyed(this.destroyRef), finalize(() => this.isIndexing.set(false)),
    ).subscribe({
      next: (res) => {
        this.statusMessage.set(res.message || `知識庫「${name}」已成功刪除。`);
        this.collectionDeleted.emit(name);
        this.refresh();
      },
      error: error => this.failOperation(error),
    });
  }
  private beginOperation(message: string): void {
    this.errorMessage.set(null); this.indexingStats.set(null); this.statusMessage.set(message); this.isIndexing.set(true);
  }
  private finishIndex(stats: IndexStats): void {
    this.indexingStats.set(stats); this.statusMessage.set('索引作業完成'); this.indexCompleted.emit(stats); this.refresh();
  }
  private failOperation(error: unknown): void {
    this.errorMessage.set(mdQueryError(error)); this.statusMessage.set('索引或註冊失敗；已註冊的集合可從列表重試索引。'); this.refresh();
  }
  constructor() {
  }
}
